import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Share2, Lock, X, CreditCard, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// PaymentModal is commented out — payment is now handled by Paynkolay's
// hosted co-payment page (Pay By Link / Ortak Ödeme Sayfası).
// When the user clicks "Unlock", the backend calls /Vpos/by-link-create and
// returns a hosted URL that we redirect the user to directly.
// ─────────────────────────────────────────────────────────────────────────────

// function PaymentModal({ isOpen, onClose, onPay, quiz, t, unlocking }) {
//   const [cardHolder, setCardHolder] = useState('');
//   const [cardNumber, setCardNumber] = useState('');
//   const [expiry, setExpiry] = useState('');
//   const [cvv, setCvv] = useState('');
//   const [focused, setFocused] = useState(null);
//   const modalRef = useRef(null);

//   const isFormComplete =
//     cardHolder.trim().length >= 2 &&
//     cardNumber.replace(/\s/g, '').length === 16 &&
//     expiry.length === 5 &&
//     cvv.length >= 3;

//   const formatCardNumber = (val) => {
//     const digits = val.replace(/\D/g, '').slice(0, 16);
//     return digits.replace(/(.{4})/g, '$1 ').trim();
//   };

//   const formatExpiry = (val) => {
//     const digits = val.replace(/\D/g, '').slice(0, 4);
//     if (digits.length >= 3) return digits.slice(0, 2) + '/' + digits.slice(2);
//     return digits;
//   };

//   const handleCardNumberChange = (e) => {
//     setCardNumber(formatCardNumber(e.target.value));
//   };

//   const handleExpiryChange = (e) => {
//     const raw = e.target.value.replace(/\D/g, '');
//     setExpiry(formatExpiry(raw));
//   };

//   const handleCvvChange = (e) => {
//     setCvv(e.target.value.replace(/\D/g, '').slice(0, 4));
//   };

//   const handleBackdropClick = (e) => {
//     if (e.target === e.currentTarget) onClose();
//   };

//   useEffect(() => {
//     const handler = (e) => { if (e.key === 'Escape') onClose(); };
//     if (isOpen) document.addEventListener('keydown', handler);
//     return () => document.removeEventListener('keydown', handler);
//   }, [isOpen, onClose]);

//   const getCardType = () => {
//     const num = cardNumber.replace(/\s/g, '');
//     if (/^4/.test(num)) return 'visa';
//     if (/^5[1-5]/.test(num) || /^2[2-7]/.test(num)) return 'mastercard';
//     if (/^3[47]/.test(num)) return 'amex';
//     return null;
//   };

//   const cardType = getCardType();

//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           onClick={handleBackdropClick}
//           style={{ background: 'rgba(15, 10, 30, 0.7)', backdropFilter: 'blur(6px)' }}
//         >
//           <motion.div
//             ref={modalRef}
//             initial={{ opacity: 0, y: 60, scale: 0.97 }}
//             animate={{ opacity: 1, y: 0, scale: 1 }}
//             exit={{ opacity: 0, y: 60, scale: 0.97 }}
//             transition={{ type: 'spring', damping: 28, stiffness: 340 }}
//             className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
//             style={{ maxHeight: '95dvh', overflowY: 'auto' }}
//           >
//             <div className="h-1.5 w-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-400" />
//             <div className="p-6 sm:p-8">
//               <div className="flex items-start justify-between mb-6">
//                 <div>
//                   <div className="flex items-center gap-2 mb-1">
//                     <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
//                       <CreditCard className="w-4 h-4 text-white" />
//                     </div>
//                     <h2 className="text-xl font-bold text-slate-900">{t('payment.title')}</h2>
//                   </div>
//                   <p className="text-sm text-slate-500 ml-10">{t('payment.subtitle')}</p>
//                 </div>
//                 <button
//                   onClick={onClose}
//                   className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors flex-shrink-0 mt-0.5"
//                 >
//                   <X className="w-4 h-4 text-slate-500" />
//                 </button>
//               </div>
//               {/* ... (rest of modal JSX omitted) ... */}
//             </div>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// }

export default function ResultsPage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [userResult, setUserResult] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [result, setResult] = useState(null);
  const [traits, setTraits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  // const [showPaymentModal, setShowPaymentModal] = useState(false); // no longer used

  useEffect(() => {
    fetchResultData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const fetchResultData = async () => {
    setLoading(true);
    try {
      const userResultRes = await axios.get(
        `${BACKEND_URL}/api/user-results/${uid}`
      );
      const userResultData = userResultRes.data;
      setUserResult(userResultData);

      const [quizRes, resultsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/quizzes/${userResultData.quiz_id}`),
        axios.get(
          `${BACKEND_URL}/api/quizzes/${userResultData.quiz_id}/results`
        ),
      ]);

      setQuiz(quizRes.data);

      const matchingResult = resultsRes.data.find(
        r => r.result_id === userResultData.result_id
      );
      setResult(matchingResult);

      if (userResultData.results_unlocked) {
        const traitsRes = await axios.get(
          `${BACKEND_URL}/api/quizzes/${userResultData.quiz_id}/traits?result_id=${userResultData.result_id}`
        );
        setTraits(traitsRes.data);
      } else {
        setTraits([]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  // Clicking unlock → directly calls backend to get Paynkolay co-payment URL,
  // then redirects the user there. No modal is shown.
	const handleUnlock = async () => {
	  setUnlocking(true);
	  try {
		const response = await axios.post(
		  `${BACKEND_URL}/api/user-results/${uid}/create-payment`
		);
		const { redirectUrl } = response.data;
		if (redirectUrl) {
		  window.location.href = redirectUrl;
		} else {
		  toast.error('Failed to create payment form');
		  setUnlocking(false);
		}
	  } catch (err) {
		console.error(err);
		toast.error('Failed to initiate payment');
		setUnlocking(false);
	  }
	};

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t('results.linkCopied'));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-600 text-sm">
            Loading results…
          </p>
        </div>
      </div>
    );
  }

  const allTraitsLocked = !userResult?.results_unlocked;

  return (
    <>
    {/* PaymentModal removed — Paynkolay hosted page handles payment UI */}
    {/* <PaymentModal
      isOpen={showPaymentModal}
      onClose={() => setShowPaymentModal(false)}
      onPay={handlePaymentSubmit}
      quiz={quiz}
      t={t}
      unlocking={unlocking}
    /> */}
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-14">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('results.backToQuizzes')}
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <Share2 className="w-4 h-4" />
            {t('results.shareResult')}
          </button>
        </div>

        {/* Result card (hidden if all locked) */}
        {!allTraitsLocked && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 backdrop-blur-md rounded-3xl p-6 sm:p-8 border border-white/50 shadow-xl mb-8"
          >
            <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-2">
              {t('results.yourResult')}
            </p>

            <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 mb-4">
              {result?.result_name}
            </h1>

            <p className="text-sm sm:text-base text-slate-600 mb-6">
              {result?.result_text}
            </p>

            {result?.result_image && (
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={result.result_image}
                  alt={result.result_name}
                  className="w-full h-48 sm:h-64 object-cover"
                />
              </div>
            )}
          </motion.div>
        )}

        {/* Traits */}
{userResult?.results_unlocked && traits.length > 0 && (
  <div className="space-y-6">
    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
      {t('results.unlockedTraits')}
    </h2>

    {traits.map((trait, idx) => (
      <motion.div
        key={trait.trait_id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: idx * 0.05 }}
        className="bg-white/80 backdrop-blur-md rounded-2xl p-5 sm:p-6 border border-white/50 shadow-lg flex items-start gap-4 sm:gap-6"
      >
        {/* Trait Image - Square box with rounded corners */}
        <div className="flex-shrink-0">
          <img
            src={trait.trait_image}
            alt={trait.trait_name}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shadow-md"
          />
        </div>

        {/* Trait Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
            {trait.trait_name}
          </h3>
          <p className="text-sm sm:text-base text-slate-600">
            {trait.trait_text}
          </p>
        </div>
      </motion.div>
    ))}
  </div>
)}

        {/* Unlock section */}
        {!userResult?.results_unlocked && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 bg-white/70 backdrop-blur-md rounded-3xl p-6 sm:p-8 border border-white/50 shadow-xl"
          >
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                {t('results.unlockMessage')}
              </h2>

              <p className="text-md text-slate-600 mb-6">
                {t('results.price')} {quiz?.quiz_price} USD
              </p>

              {/* Clicking this button now redirects directly to Paynkolay's
                  hosted co-payment page instead of opening a local modal */}
              <button
                onClick={handleUnlock}
                disabled={unlocking}
                className="w-full h-11 rounded-full text-sm font-bold text-white shadow-md bg-gradient-to-r from-violet-500 to-fuchsia-500 disabled:opacity-60 hover:scale-105 transition-transform"
              >
                {unlocking ? t('results.redirecting') : t('results.unlockButton')}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
}